import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Image as ImageIcon, Send, Loader2, Sparkles, Trash2, Camera, Clipboard, Pencil, Video, Square, PlayCircle, Film } from 'lucide-react';
import OtterAvatar from './OtterAvatar';
import ScreenshotAnnotator from './ScreenshotAnnotator';
import { uploadFeedbackScreenshot, uploadFeedbackVideo, createFeedback } from '../services/api';

const TYPE_OPTIONS = [
  { value: 'bug', label: '🐛 Bug', color: 'red' },
  { value: 'amelioration', label: '✨ Amélioration', color: 'amber' },
  { value: 'feature', label: '💡 Nouvelle fonctionnalité', color: 'indigo' },
];

const FeedbackModal = ({ onClose, pageUrl, pageLabel }) => {
  const [description, setDescription] = useState('');
  const [typeDemande, setTypeDemande] = useState('amelioration');
  const [screenshots, setScreenshots] = useState([]); // { path, preview }
  const [videos, setVideos] = useState([]); // { path, preview, size }
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [capturing, setCapturing] = useState(false);
  const [hiddenForCapture, setHiddenForCapture] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // shows preview after generation
  const [annotatingIdx, setAnnotatingIdx] = useState(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Mobile : pas de capture d'écran navigateur (getDisplayMedia absent) → galerie / appareil photo
  const isMobile = typeof navigator !== 'undefined' && (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || !navigator.mediaDevices?.getDisplayMedia);
  const mediaRecorderRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const MAX_RECORD_SECONDS = 60;

  // Upload a Blob (from clipboard or screen capture) as if it were a file
  const uploadBlob = async (blob, filename = 'capture.png') => {
    if (!blob) return;
    if (blob.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10 Mo)');
      return;
    }
    setUploading(true);
    try {
      const file = new File([blob], filename, { type: blob.type || 'image/png' });
      const preview = URL.createObjectURL(blob);
      const res = await uploadFeedbackScreenshot(file);
      setScreenshots((prev) => [...prev, { path: res.data.path, preview, size: res.data.size }]);
      toast.success('Capture ajoutée 🦦');
    } catch (e) {
      toast.error('Échec upload');
    } finally {
      setUploading(false);
    }
  };

  // === Paste from clipboard (Ctrl+V / Cmd+V) ===
  // After Win+Shift+S, the image is in the clipboard. The user just pastes.
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) await uploadBlob(blob, `paste-${Date.now()}.png`);
          return;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  // Toggle a global class on <html> so the floating launcher button + modal
  // are visually removed during the screen capture (otherwise the Loutre
  // appears in the captured frame instead of the underlying page).
  useEffect(() => {
    const root = document.documentElement;
    if (hiddenForCapture) {
      root.classList.add('feedback-capturing');
    } else {
      root.classList.remove('feedback-capturing');
    }
    return () => root.classList.remove('feedback-capturing');
  }, [hiddenForCapture]);

  // === Native browser screen capture (equivalent of Windows snipping tool) ===
  // Opens the OS prompt to pick a window/screen, grabs ONE frame as PNG.
  const handleScreenCapture = async () => {
    const inIframe = window !== window.top;

    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error(
        "Votre navigateur ne supporte pas la capture intégrée. Utilisez Win+Shift+S puis Ctrl+V dans cette fenêtre.",
        { duration: 6000 }
      );
      return;
    }

    setCapturing(true);
    // Hide the modal BEFORE the OS picker opens so the captured frame
    // contains the underlying page, not the Loutre overlay itself.
    setHiddenForCapture(true);
    // Wait 2 animation frames + small delay so the DOM repaint is committed
    // before the browser snapshots the tab for its picker thumbnail.
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 80)))
    );
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always', frameRate: 30 },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;

      // Wait for the video to have actual content (not just metadata).
      // We listen for 'loadeddata' (frame ready) + requestVideoFrameCallback when supported.
      await new Promise((resolve) => {
        const onReady = () => resolve();
        video.addEventListener('loadeddata', onReady, { once: true });
      });
      await video.play();

      // Use requestVideoFrameCallback when available to ensure a frame is rendered
      const waitForFrame = () =>
        new Promise((resolve) => {
          if (typeof video.requestVideoFrameCallback === 'function') {
            video.requestVideoFrameCallback(() => resolve());
          } else {
            // Fallback: 2 RAF cycles + 100ms safety
            requestAnimationFrame(() =>
              requestAnimationFrame(() => setTimeout(resolve, 100))
            );
          }
        });
      await waitForFrame();

      const w = settings.width || video.videoWidth || 1920;
      const h = settings.height || video.videoHeight || 1080;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, w, h);

      // Verify the captured frame isn't all-white/all-black (rare edge case)
      const sample = ctx.getImageData(Math.floor(w / 2), Math.floor(h / 2), 1, 1).data;
      // Stop sharing
      stream.getTracks().forEach((t) => t.stop());

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png', 0.92)
      );
      if (!blob) {
        toast.error("Impossible de créer l'image. Réessayez ou utilisez Win+Shift+S + Ctrl+V.");
        return;
      }
      // If pixel is pure white AND alpha 0, capture likely failed
      if (sample[0] === 255 && sample[1] === 255 && sample[2] === 255 && blob.size < 5000) {
        toast.error("La capture semble vide. Réessayez ou utilisez Win+Shift+S + Ctrl+V.", { duration: 6000 });
        return;
      }
      await uploadBlob(blob, `screen-${Date.now()}.png`);
    } catch (err) {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (err.name === 'NotAllowedError' && !inIframe) {
        return;
      }
      if (err.name === 'NotAllowedError' && inIframe) {
        toast.error(
          "La capture directe est bloquée dans la fenêtre de prévisualisation. Utilisez plutôt Win+Shift+S puis Ctrl+V ici, OU ouvrez l'app dans un nouvel onglet.",
          { duration: 8000 }
        );
        return;
      }
      if (err.name === 'NotSupportedError') {
        toast.error('Fonction non supportée dans cet environnement. Utilisez Win+Shift+S + Ctrl+V.', { duration: 6000 });
        return;
      }
      toast.error(`Capture impossible (${err.name || 'inconnu'}). Astuce : Win+Shift+S puis Ctrl+V ici.`, { duration: 6000 });
    } finally {
      setCapturing(false);
      setHiddenForCapture(false);
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newOnes = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} n'est pas une image`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} dépasse 10 Mo`);
        continue;
      }
      try {
        const preview = URL.createObjectURL(file);
        const res = await uploadFeedbackScreenshot(file);
        newOnes.push({ path: res.data.path, preview, size: res.data.size });
      } catch (e) {
        toast.error(`Échec upload ${file.name}`);
      }
    }
    setScreenshots((prev) => [...prev, ...newOnes]);
    setUploading(false);
  };

  const removeScreenshot = (idx) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeVideo = (idx) => {
    setVideos((prev) => {
      const target = prev[idx];
      if (target && target.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // === Video file upload (imported video) ===
  const handleVideoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error("Ce n'est pas une vidéo");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Vidéo trop volumineuse (max 50 Mo)');
      return;
    }
    await uploadVideoBlob(file, file.name || `video-${Date.now()}.webm`);
  };

  const uploadVideoBlob = async (blob, filename = `video-${Date.now()}.webm`) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const file = blob instanceof File ? blob : new File([blob], filename, { type: blob.type || 'video/webm' });
      const preview = URL.createObjectURL(blob);
      const res = await uploadFeedbackVideo(file, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      setVideos((prev) => [...prev, { path: res.data.path, preview, size: res.data.size }]);
      toast.success('Vidéo ajoutée 🎥');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec upload vidéo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // === Screen recording (MediaRecorder + getDisplayMedia) ===
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia || typeof window.MediaRecorder === 'undefined') {
      toast.error("L'enregistrement vidéo n'est pas supporté par ce navigateur.", { duration: 6000 });
      return;
    }
    const inIframe = window !== window.top;
    // Hide modal so the OS picker thumbnail + recorded frames don't show the Loutre
    setHiddenForCapture(true);
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 80)))
    );
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always', frameRate: 15 },
        audio: false,
      });
    } catch (err) {
      setHiddenForCapture(false);
      if (err.name === 'NotAllowedError' && inIframe) {
        toast.error("L'enregistrement est bloqué dans cette fenêtre. Ouvrez l'app dans un nouvel onglet.", { duration: 8000 });
      }
      // user cancelled silently otherwise
      return;
    }
    // Once recording is active, reveal the modal again so the user can hit "Stop"
    setHiddenForCapture(false);

    // Pick a supported mimeType
    let mimeType = 'video/webm;codecs=vp9';
    if (!window.MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8';
    if (!window.MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
    if (!window.MediaRecorder.isTypeSupported(mimeType)) mimeType = '';

    let recorder;
    try {
      recorder = mimeType ? new window.MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_000_000 }) : new window.MediaRecorder(stream);
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      toast.error("Impossible d'initialiser l'enregistreur");
      return;
    }

    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = async () => {
      try {
        const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
        if (blob.size > 0) {
          await uploadVideoBlob(blob, `record-${Date.now()}.webm`);
        }
      } finally {
        stream.getTracks().forEach((t) => t.stop());
        recordStreamRef.current = null;
        mediaRecorderRef.current = null;
      }
    };

    // Auto-stop if the user stops sharing via the browser bar
    stream.getVideoTracks()[0].addEventListener('ended', () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setRecording(false);
      if (recordTimerRef.current) {
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
      }
    });

    recordStreamRef.current = stream;
    mediaRecorderRef.current = recorder;
    recorder.start(1000); // collect data every second
    setRecording(true);
    setRecordTime(0);

    // Timer + auto-stop at max duration
    recordTimerRef.current = setInterval(() => {
      setRecordTime((t) => {
        const next = t + 1;
        if (next >= MAX_RECORD_SECONDS) stopRecording();
        return next;
      });
    }, 1000);

    toast.info(`Enregistrement démarré (max ${MAX_RECORD_SECONDS}s) — cliquez Stop quand vous avez fini`, { duration: 4000 });
  };

  const stopRecording = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }
    setRecording(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (recordStreamRef.current) recordStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Save annotated version: re-upload as new screenshot, replacing the original
  const handleSaveAnnotation = async (idx, blob) => {
    if (!blob) return;
    setUploading(true);
    try {
      const file = new File([blob], `annot-${Date.now()}.png`, { type: 'image/png' });
      const res = await uploadFeedbackScreenshot(file);
      const newPreview = URL.createObjectURL(blob);
      setScreenshots((prev) =>
        prev.map((s, i) =>
          i === idx
            ? { path: res.data.path, preview: newPreview, size: res.data.size }
            : s
        )
      );
      toast.success('Annotations enregistrées ✏️');
      setAnnotatingIdx(null);
    } catch (e) {
      toast.error('Échec de la sauvegarde');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (description.trim().length < 5) {
      toast.error('Décris ton idée en quelques mots (5 caractères minimum)');
      return;
    }
    if (submitting) return; // hard-guard against accidental double-click
    setSubmitting(true);
    const toastId = toast.loading('🦦 La loutre rédige ton prompt technique... (5-10s)', { duration: Infinity });
    try {
      const res = await createFeedback({
        description: description.trim(),
        type_demande: typeDemande,
        page_url: pageUrl,
        page_label: pageLabel,
        screenshots: screenshots.map((s) => s.path),
        videos: videos.map((v) => v.path),
      });
      setResult(res.data);
      // Notify any open FeedbackAdminPage to refresh its list
      window.dispatchEvent(new CustomEvent('feedback:created', { detail: res.data }));
      toast.success('✅ Idée transmise à l\'admin — prompt généré !', { id: toastId, duration: 4000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi', { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (recording) stopRecording();
    // Revoke object URLs
    screenshots.forEach((s) => {
      if (s.preview) URL.revokeObjectURL(s.preview);
    });
    videos.forEach((v) => {
      if (v.preview) URL.revokeObjectURL(v.preview);
    });
    onClose();
  };

  // SUCCESS view — show the AI-generated prompt
  if (result) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center gap-3">
              <OtterAvatar size={40} />
              <div>
                <h3 className="font-bold text-amber-900">Idée enregistrée !</h3>
                <p className="text-xs text-amber-700">L'admin pourra la valider dans "Modifications app"</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-1 hover:bg-amber-100 rounded" data-testid="feedback-success-close">
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                Prompt technique généré par l'IA
              </h4>
              {result.prompt_technique ? (
                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs whitespace-pre-wrap font-mono text-slate-800 leading-relaxed">
                  {result.prompt_technique}
                </pre>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  ⚠️ L'IA n'a pas pu générer le prompt cette fois — ta description brute est sauvegardée et l'admin pourra relancer la génération depuis "Modifications app".
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 rounded p-3">
              <strong>Statut :</strong> À traiter — en attente de validation par l'admin.
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
            <button
              onClick={handleClose}
              className="px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium"
              data-testid="feedback-done-btn"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // FORM view
  // While recording, hide the modal entirely so the user can interact with the
  // application behind. Only a small floating recording bar (Loom-style) stays
  // visible with the timer + stop button.
  if (recording) {
    return (
      <div
        className="fixed bottom-6 right-6 z-[60] bg-slate-900 text-white rounded-2xl shadow-2xl border border-red-500/40 px-4 py-3 flex items-center gap-3 animate-pulse-slow"
        style={{ boxShadow: '0 8px 32px rgba(239, 68, 68, 0.4)' }}
        data-testid="feedback-recording-bar"
      >
        <div className="relative flex items-center">
          <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="absolute w-3 h-3 bg-red-500 rounded-full animate-ping opacity-75" />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-red-300 font-semibold">Enregistrement</span>
          <span className="font-mono font-bold text-sm">
            {Math.floor(recordTime / 60)}:{String(recordTime % 60).padStart(2, '0')}
            <span className="text-slate-400 font-normal text-xs"> / {Math.floor(MAX_RECORD_SECONDS / 60)}:{String(MAX_RECORD_SECONDS % 60).padStart(2, '0')}</span>
          </span>
        </div>
        <button
          onClick={stopRecording}
          data-testid="feedback-stop-record-btn"
          className="ml-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg flex items-center gap-1.5 shadow"
          title="Arrêter l'enregistrement"
        >
          <Square className="w-4 h-4 fill-current" />
          Stop
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 transition-opacity duration-100"
      style={hiddenForCapture ? { opacity: 0, pointerEvents: 'none', visibility: 'hidden' } : undefined}
      aria-hidden={hiddenForCapture ? 'true' : undefined}
      data-testid="feedback-modal-wrapper"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <OtterAvatar size={42} animate />
            <div>
              <h3 className="font-bold text-amber-900">Une idée ? Un bug ?</h3>
              <p className="text-xs text-amber-700">La loutre s'occupe de tout transmettre à l'admin 🦦</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-amber-100 rounded"
            data-testid="feedback-close-btn"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Page context */}
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded p-2.5">
            <span className="font-medium text-slate-700">Page :</span>
            <span className="font-mono">{pageLabel}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-400 truncate">{pageUrl}</span>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTypeDemande(opt.value)}
                  data-testid={`feedback-type-${opt.value}`}
                  className={`px-3 py-1.5 rounded-full border text-sm transition ${
                    typeDemande === opt.value
                      ? `bg-${opt.color}-100 border-${opt.color}-400 text-${opt.color}-800 font-semibold`
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Décris ton idée avec tes mots
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex : Sur la page Marge, quand je clique sur l'étoile d'un devis, ça me met une erreur. Avant ça marchait."
              rows={5}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
              data-testid="feedback-description-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              L'IA va reformuler ta description en un prompt technique structuré, prêt à être envoyé à Emergent.
            </p>
          </div>

          {/* Screenshots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">
                Captures d'écran <span className="text-slate-400 font-normal">(optionnel)</span>
              </label>
              {!isMobile && <span className="text-[11px] text-slate-400">💡 Astuce : Win+Shift+S puis <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded text-[10px]">Ctrl+V</kbd> ici</span>}
            </div>

            {/* Capture action buttons */}
            <div className="flex flex-wrap gap-2 mb-3">
              {isMobile ? (
                <>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="feedback-gallery-btn"
                    className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 font-medium shadow"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Capture depuis la galerie
                  </button>
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="feedback-camera-btn"
                    className="px-3 py-2 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Camera className="w-4 h-4" />
                    Prendre une photo
                  </button>
                  <p className="w-full text-[11px] text-slate-500" data-testid="feedback-mobile-hint">
                    💡 Faites une capture d'écran du téléphone (boutons latéraux), puis importez-la depuis la galerie.
                  </p>
                </>
              ) : (
                <>
              <button
                type="button"
                onClick={handleScreenCapture}
                disabled={capturing || uploading}
                data-testid="feedback-capture-btn"
                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 font-medium shadow"
              >
                {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                Capturer un écran
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="feedback-upload-btn"
                className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
              >
                <ImageIcon className="w-4 h-4" />
                Importer un fichier
              </button>
              <div className="px-3 py-1.5 bg-slate-50 border border-dashed border-slate-300 text-slate-500 text-xs rounded-lg flex items-center gap-1.5">
                <Clipboard className="w-3.5 h-3.5" />
                Ou coller depuis le presse-papier
              </div>
                </>
              )}
            </div>

            {/* Existing screenshots preview */}
            {screenshots.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {screenshots.map((s, idx) => (
                  <div key={idx} className="relative w-24 h-24 rounded border border-slate-300 overflow-hidden group">
                    <img src={s.preview} alt={`Capture ${idx + 1}`} className="w-full h-full object-cover" />
                    <div className={`absolute inset-0 transition flex items-center justify-center gap-1 ${isMobile ? 'bg-black/30 opacity-100' : 'bg-black/0 group-hover:bg-black/40 opacity-0 group-hover:opacity-100'}`}>
                      <button
                        onClick={() => setAnnotatingIdx(idx)}
                        className="bg-amber-500 hover:bg-amber-600 text-white rounded p-1.5"
                        title="Annoter / Recadrer"
                        data-testid={`annotate-btn-${idx}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => removeScreenshot(idx)}
                        className="bg-red-500 hover:bg-red-600 text-white rounded p-1.5"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {uploading && (
                  <div className="w-24 h-24 rounded border border-dashed border-amber-300 bg-amber-50 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                  </div>
                )}
              </div>
            ) : uploading ? (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded">
                <Loader2 className="w-4 h-4 animate-spin" /> Upload en cours…
              </div>
            ) : null}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="feedback-file-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="feedback-camera-input"
            />
          </div>

          {/* Videos (screen recording + import) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">
                Vidéo <span className="text-slate-400 font-normal">(optionnel, max 60s ou 50 Mo)</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {isMobile ? (
                <>
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="feedback-import-video-btn"
                    className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5 font-medium shadow"
                  >
                    <Film className="w-4 h-4" />
                    Vidéo depuis la galerie
                  </button>
                  <p className="w-full text-[11px] text-slate-500" data-testid="feedback-mobile-video-hint">
                    💡 Enregistrez l'écran avec l'outil du téléphone (iPhone : Centre de contrôle → Enregistrement de l'écran), puis importez la vidéo ici.
                  </p>
                </>
              ) : (
                <>
              {!recording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploading}
                  data-testid="feedback-record-btn"
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5 font-medium shadow"
                >
                  <Video className="w-4 h-4" />
                  Enregistrer une vidéo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  data-testid="feedback-stop-record-btn"
                  className="px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 flex items-center gap-2 font-medium shadow animate-pulse"
                >
                  <Square className="w-4 h-4 fill-current" />
                  Stop · {Math.floor(recordTime / 60)}:{String(recordTime % 60).padStart(2, '0')} / {Math.floor(MAX_RECORD_SECONDS / 60)}:{String(MAX_RECORD_SECONDS % 60).padStart(2, '0')}
                </button>
              )}
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploading || recording}
                data-testid="feedback-import-video-btn"
                className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Film className="w-4 h-4" />
                Importer une vidéo
              </button>
                </>
              )}
              {uploading && uploadProgress > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Upload {uploadProgress}%
                </div>
              )}
            </div>

            {videos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {videos.map((v, idx) => (
                  <div key={idx} className="relative w-32 h-20 rounded border border-slate-300 overflow-hidden group bg-slate-900">
                    <video
                      src={v.preview}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <PlayCircle className="w-7 h-7 text-white/80 drop-shadow" />
                    </div>
                    <div className="absolute top-1 left-1 px-1 py-0.5 bg-black/60 text-white text-[9px] rounded">
                      {v.size ? (v.size / (1024 * 1024)).toFixed(1) + ' Mo' : 'Vidéo'}
                    </div>
                    <button
                      onClick={() => removeVideo(idx)}
                      className={`absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center transition ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => handleVideoFile(e.target.files?.[0])}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex flex-col gap-3">
          {submitting && (
            <div
              className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-900 text-sm"
              data-testid="submit-progress-banner"
            >
              <Loader2 className="w-4 h-4 animate-spin text-amber-600 shrink-0" />
              <span className="flex-1">
                <strong>Patience !</strong> Claude rédige un prompt technique structuré pour Emergent. Cela prend généralement 5 à 10 secondes.
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || description.trim().length < 5}
            data-testid="feedback-submit-btn"
            className={`px-5 py-2 text-white rounded-lg flex items-center gap-2 font-medium transition-all ${
              submitting
                ? 'bg-amber-700 cursor-wait shadow-lg scale-105'
                : 'bg-amber-600 hover:bg-amber-700 disabled:opacity-50'
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-semibold">La loutre rédige le prompt...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Envoyer à l'admin
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      {/* Screenshot Annotator overlay */}
      {annotatingIdx !== null && screenshots[annotatingIdx] && (
        <ScreenshotAnnotator
          src={screenshots[annotatingIdx].preview}
          onClose={() => setAnnotatingIdx(null)}
          onSave={(blob) => handleSaveAnnotation(annotatingIdx, blob)}
        />
      )}
    </div>
  );
};

export default FeedbackModal;
